import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SearchInput } from '@/components/ui/input'
import { HubConnectionState } from '@microsoft/signalr'
import { Divider, Menu } from 'antd'
import classNames from 'classnames'
import Profile from 'components/Avatar/Profile'
import { HeaderLogoIcon } from 'components/Icon'
import { Link } from 'components/Link'
import {
  Blocks,
  ChartColumn,
  FileCheckCorner,
  Landmark,
  Megaphone,
  Settings,
  Ticket,
  Users,
} from 'lucide-react'
import { appENV, checkWindowLocation } from 'utils/dom'

import { TestStatusTabs } from './api/Clients/Types/AppropriatenessTestDto'
import { PermissionDto } from './api/Identity/Types/PermissionDto'
import { PermissionsEnum } from './api/Identity/Types/PermissionsEnum'
import { DividendsTab } from './routes/Brokerage/Dividends/DividendsNavigation'
import { TicketsViewType } from './routes/Tickets/TicketsViewType'
import { appTestRouteUrl, isFeatureEnabled, links } from './utils/featureToggles'
import { hasPermission, hasSomePermissions } from './utils/permissionsValidator'
import { TenantFeature } from './utils/Tenant'
import { calculateTotalAmountOfTickets } from './utils/ticket.utils'

import styles from './Nav.module.scss'

interface Props {
  permissions: PermissionDto[]
  tickets: Record<number, number>
  status?: HubConnectionState
}

export const Nav: React.FC<Props> = ({ permissions, tickets, status }) => {
  const navigate = useNavigate()
  const onSearch = (searchQuery: string) => {
    navigate('/search?q=' + encodeURIComponent(searchQuery))
  }

  useEffect(() => {
    document.title = `Tickmill Backoffice (${calculateTotalAmountOfTickets(tickets)})`
  }, [tickets])

  return (
    <nav
      className={classNames(styles.nav, styles.menu, {
        [styles.production]: checkWindowLocation() === appENV.production,
        [styles.test]: checkWindowLocation() === appENV.test,
        [styles.development]:
          checkWindowLocation() === appENV.localhost ||
          checkWindowLocation() === appENV.development,
      })}
      role='navigation'
      aria-label='main navigation'
    >
      <div className={classNames(styles.leftBlock)}>
        <div className={classNames(styles.logo)}>
          <Link to={links.dashboard.url}>
            <HeaderLogoIcon />
          </Link>
        </div>
        <div className={classNames(styles.search)}>
          <SearchInput placeholder='Search' onSearch={(query) => onSearch(query)} />
        </div>
      </div>
      <div className={classNames(styles.rightBlock)}>
        <div className={classNames(styles.menu)}>
          <MainMenu permissions={permissions} tickets={tickets} />
        </div>
        <span className={classNames(styles.dividerRight)} />
        <div className={classNames(styles.profile)}>
          <Profile triggerMenuOnClick status={status} />
        </div>
        <span className={classNames(styles.dividerRight)} />
      </div>
    </nav>
  )
}

interface MainMenuProps {
  permissions: PermissionDto[]
  tickets: Record<number, number>
}

const MainMenu: React.FC<MainMenuProps> = ({ permissions, tickets }) => {
  const myTicketsCount = calculateTotalAmountOfTickets(tickets)
  const location = useLocation()

  const hasRecordsMenuItems =
    (isFeatureEnabled(TenantFeature.recordClients) &&
      hasPermission(permissions, PermissionsEnum.CanBrowseClients)) ||
    (isFeatureEnabled(TenantFeature.recordsWallets) &&
      hasPermission(permissions, PermissionsEnum.CanBrowseWallets)) ||
    hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccounts) ||
    hasPermission(permissions, PermissionsEnum.CanBrowseDemoAccounts) ||
    hasPermission(permissions, PermissionsEnum.CanBrowseWelcomeAccounts) ||
    (isFeatureEnabled(TenantFeature.recordsAccountGroups) &&
      hasPermission(permissions, PermissionsEnum.CanBrowseAccountGroups)) ||
    (isFeatureEnabled(TenantFeature.recordsIntroducingBrokers) &&
      hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokers)) ||
    (isFeatureEnabled(TenantFeature.recordsPaymentAgents) &&
      hasPermission(permissions, PermissionsEnum.CanBrowsePaymentAgents)) ||
    (isFeatureEnabled(TenantFeature.recordLeads) &&
      hasPermission(permissions, PermissionsEnum.CanViewLeads)) ||
    (isFeatureEnabled(TenantFeature.recordsExchangeRates) &&
      hasPermission(permissions, PermissionsEnum.CanBrowseExchangeRates))

  const hasPaymentToolsMenuItems =
    (isFeatureEnabled(TenantFeature.administrationPaymentControls) &&
      hasPermission(permissions, PermissionsEnum.CanViewPaymentControls) &&
      ((isFeatureEnabled(TenantFeature.administrationPaymentControlsPSPCheckState) &&
        hasPermission(permissions, PermissionsEnum.CanViewPaymentProviderOptions)) ||
        (isFeatureEnabled(TenantFeature.administrationAllowedPaymentProvidersMatrix) &&
          hasPermission(permissions, PermissionsEnum.CanBrowsePspGroups)))) ||
    (isFeatureEnabled(TenantFeature.administrationInterestRateConfigControls) &&
      hasPermission(permissions, PermissionsEnum.CanBrowseInterestRatesConfig)) ||
    (isFeatureEnabled(TenantFeature.administrationMlWithdrawalAutomationGlobalControls) &&
      hasPermission(permissions, PermissionsEnum.CanViewMLWithdrawalAutomationSettings)) ||
    (isFeatureEnabled(TenantFeature.administrationWithdrawalThresholds) &&
      hasPermission(permissions, PermissionsEnum.CanViewPaymentProviderThresholds)) ||
    isFeatureEnabled(TenantFeature.administrationTransactionProcessingMetrics)

  return (
    <Menu
      overflowedIndicator={false}
      theme='light'
      mode='horizontal'
      selectedKeys={[location.pathname.split('/')[2]]}
      style={{ background: 'transparent' }}
    >
      {hasRecordsMenuItems && (
        <Menu.SubMenu
          className={styles.subMenu}
          title={
            <span data-test='records-menu-link' className={classNames(styles.menuItem)}>
              <span className={classNames(styles.menuIcon)}>
                <Users size={21} />
              </span>
              <span className={classNames(styles.menuText)}>Records</span>
            </span>
          }
        >
          {isFeatureEnabled(TenantFeature.recordClients) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseClients) && (
              <Menu.Item key='clients' data-test='records-clients-menu-link'>
                <Link to={links.recordsClients.url}>Clients</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.recordsWallets) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseWallets) && (
              <Menu.Item key='wallets' data-test='records-wallets-menu-link'>
                <Link to={links.recordsWallets.url}>Wallets</Link>
              </Menu.Item>
            )}
          {(isFeatureEnabled(TenantFeature.recordsTradingAccounts) ||
            hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccounts) ||
            hasPermission(permissions, PermissionsEnum.CanBrowseDemoAccounts) ||
            hasPermission(permissions, PermissionsEnum.CanBrowseWelcomeAccounts)) && (
            <Menu.SubMenu
              key='trading-accounts'
              title={
                <span data-test='records-trading-accounts-menu-link'>
                  <span className={classNames(styles.menuItem)}>
                    <span className={classNames(styles.menuText)}>Trading Accounts</span>
                  </span>
                </span>
              }
            >
              {hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccounts) && (
                <Menu.Item
                  key='trading-accounts'
                  data-test='records-trading-accounts-live-menu-link'
                >
                  <Link to={links.recordsTradingAccounts.url}>Live</Link>
                </Menu.Item>
              )}
              {hasPermission(permissions, PermissionsEnum.CanBrowseDemoAccounts) && (
                <Menu.Item key='demo-accounts' data-test='records-trading-accounts-demo-menu-link'>
                  <Link to={links.recordsDemoAccounts.url}>Demo</Link>
                </Menu.Item>
              )}
              {hasPermission(permissions, PermissionsEnum.CanBrowseWelcomeAccounts) && (
                <Menu.Item
                  key='welcome-accounts'
                  data-test='records-trading-accounts-welcome-menu-link'
                >
                  <Link to={links.recordsWelcomeAccounts.url}>Welcome</Link>
                </Menu.Item>
              )}
            </Menu.SubMenu>
          )}
          {isFeatureEnabled(TenantFeature.recordsAccountGroups) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseAccountGroups) && (
              <Menu.Item key='account-groups' data-test='records-account-groups-menu-link'>
                <Link to={links.recordsAccountGroups.url}>Account Groups</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.recordsIntroducingBrokers) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokers) && (
              <Menu.Item
                key='introducing-brokers'
                data-test='records-introducing-brokers-menu-link'
              >
                <Link to={links.recordsIntroducingBrokers.url}>Introducing Brokers</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.recordsExchangeRates) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseAffiliates) && (
              <Menu.Item key='affiliates' data-test='records-affiliates-menu-link'>
                <Link to={links.recordsAffiliates.url}>Affiliates</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.recordsPaymentAgents) &&
            hasPermission(permissions, PermissionsEnum.CanBrowsePaymentAgents) && (
              <Menu.Item key='payment-agents' data-test='records-payment-agents-menu-link'>
                <Link to={links.recordsPaymentAgents.url}>Payment Agents</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.recordLeads) &&
            hasPermission(permissions, PermissionsEnum.CanViewLeads) && (
              <Menu.Item key='leads' data-test='records-leads-menu-link'>
                <Link to={links.recordsLeads.url}>Leads</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.recordsExchangeRates) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseExchangeRates) && (
              <Menu.Item key='exchange-rates' data-test='records-exchange-rates-menu-link'>
                <Link to={links.recordsExchangeRates.url}>Exchange Rates</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.recordsCurrencyMarkups) &&
            hasPermission(permissions, PermissionsEnum.CanEditMarkup) && (
              <Menu.Item key='currency-markups' data-test='records-currency-markups-menu-link'>
                <Link to={links.recordsCurrencyMarkups.url}>Currency Markups</Link>
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}

      {(hasPermission(permissions, PermissionsEnum.CanBrowsePaymentsTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseBackofficeTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseTranslationsTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseBrokerageTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseComplianceTickets)) && (
        <Menu.SubMenu
          className={styles.subMenu}
          title={
            <span data-test='tickets-menu-link'>
              <span className={classNames(styles.menuItem)}>
                <span className={classNames(styles.menuIcon)}>
                  <Ticket />
                </span>
                <span className={classNames(styles.menuText)}>
                  Tickets
                  {calculateTotalAmountOfTickets(tickets) > 0 ? (
                    <span
                      style={{
                        paddingLeft: '0.2rem',
                        color: 'red',
                        fontWeight: 'bold',
                      }}
                    >
                      ({calculateTotalAmountOfTickets(tickets)})
                    </span>
                  ) : null}
                </span>
              </span>
            </span>
          }
        >
          {isFeatureEnabled(TenantFeature.ticketsPayments) &&
            hasPermission(permissions, PermissionsEnum.CanBrowsePaymentsTickets) && (
              <Menu.Item
                style={{ display: 'flex', justifyContent: 'space-between' }}
                key='payments'
                data-test='tickets-payments-menu-link'
              >
                <Link to={links.ticketsPayments.url}>Payments</Link>
                {tickets[TicketsViewType.Payments] > 0 && (
                  <span
                    style={{
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ({tickets[TicketsViewType.Payments]})
                  </span>
                )}
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.ticketsBackOffice) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseBackofficeTickets) && (
              <Menu.Item
                style={{ display: 'flex', justifyContent: 'space-between' }}
                key='backoffice'
                data-test='tickets-backoffice-menu-link'
              >
                <Link to={links.ticketsBackoffice.url}>Backoffice</Link>
                {tickets[TicketsViewType.BackOffice] > 0 && (
                  <span
                    style={{
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ({tickets[TicketsViewType.BackOffice]})
                  </span>
                )}
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.ticketsCompliance) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseComplianceTickets) && (
              <Menu.Item
                style={{ display: 'flex', justifyContent: 'space-between' }}
                key='compliance'
                data-test='tickets-compliance-menu-link'
              >
                <Link to={links.ticketsCompliance.url}>Compliance</Link>
                {tickets[TicketsViewType.Compliance] > 0 && (
                  <span
                    style={{
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ({tickets[TicketsViewType.Compliance]})
                  </span>
                )}
              </Menu.Item>
            )}

          {isFeatureEnabled(TenantFeature.ticketsTranslation) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseTranslationsTickets) && (
              <Menu.Item
                style={{ display: 'flex', justifyContent: 'space-between' }}
                key='translations'
                data-test='translations-tickets-menu-link'
              >
                <Link to={links.ticketsTranslation.url}>Translations</Link>
                {tickets[TicketsViewType.Translation] > 0 && (
                  <span
                    style={{
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ({tickets[TicketsViewType.Translation]})
                  </span>
                )}
              </Menu.Item>
            )}

          {isFeatureEnabled(TenantFeature.ticketsBrokerage) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseBrokerageTickets) && (
              <Menu.Item key='brokerage' data-test='tickets-brokerage-menu-link'>
                <Link to={links.ticketsBrokerage.url}>Brokerage</Link>
                {tickets[TicketsViewType.Brokerage] > 0 && (
                  <span
                    style={{
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ({tickets[TicketsViewType.Brokerage]})
                  </span>
                )}
              </Menu.Item>
            )}

          {isFeatureEnabled(TenantFeature.ticketsSales) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseSalesTickets) && (
              <Menu.Item
                style={{ display: 'flex', justifyContent: 'space-between' }}
                key='sales'
                data-test='tickets-sales-menu-link'
              >
                <Link to={links.ticketsSales.url}>Sales</Link>
                {tickets[TicketsViewType.Sales] > 0 && (
                  <span
                    style={{
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ({tickets[TicketsViewType.Sales]})
                  </span>
                )}
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.ticketsMyTickets) &&
            hasPermission(permissions, PermissionsEnum.CanViewTicket) && (
              <Menu.Item
                style={{ display: 'flex', justifyContent: 'space-between' }}
                key='my-tickets'
                data-test='tickets-myTickets-menu-link'
              >
                <Link to={links.ticketsMyTickets.url}>My Tickets</Link>

                {myTicketsCount > 0 && (
                  <span
                    style={{
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ({myTicketsCount})
                  </span>
                )}
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}
      {(hasPermission(permissions, PermissionsEnum.CanBrowseCustomUserWalletTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccountAdjustments) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseCrossWalletTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseWalletTradingAccountTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseWalletDepositWithdrawalTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowsePaymentAgentTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanCreateTransactions)) && (
        <Menu.SubMenu
          className={styles.subMenu}
          key='transactions'
          title={
            <span data-test='transactions-menu-link'>
              <span className={classNames(styles.menuItem)}>
                <span className={classNames(styles.menuIcon)}>
                  <Landmark />
                </span>
                <span className={classNames(styles.menuText)}>Transactions</span>
              </span>
            </span>
          }
        >
          {hasPermission(permissions, PermissionsEnum.CanBrowseCustomUserWalletTransactions) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccountAdjustments) && (
              <Menu.SubMenu
                title={<span data-test='transactions-adjustment-menu-link'>Adjustments</span>}
              >
                {isFeatureEnabled(TenantFeature.transactionWalletAdjustments) &&
                  hasPermission(
                    permissions,
                    PermissionsEnum.CanBrowseCustomUserWalletTransactions
                  ) && (
                    <Menu.Item
                      key='wallet-adjustments'
                      data-test='transactions-wallet-adjustments-menu-link'
                    >
                      <Link to={links.transactionWalletAdjustments.url}>Wallets</Link>
                    </Menu.Item>
                  )}
                {isFeatureEnabled(TenantFeature.transactionTradingAccountAdjustments) &&
                  hasPermission(
                    permissions,
                    PermissionsEnum.CanBrowseTradingAccountAdjustments
                  ) && (
                    <Menu.Item
                      key='trading-account-adjustments'
                      data-test='transactions-trading-account-adjustments-menu-link'
                    >
                      <Link to={links.transactionTradingAccountAdjustments.url}>
                        Trading Accounts
                      </Link>
                    </Menu.Item>
                  )}
              </Menu.SubMenu>
            )}
          {isFeatureEnabled(TenantFeature.transactionWallet) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseCrossWalletTransactions) && (
              <Menu.Item key='wallet' data-test='transactions-wallet-transfers-menu-link'>
                <Link to={links.transactionWallet.url}>Wallet Transfers</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.transactionPlatform) &&
            hasPermission(
              permissions,
              PermissionsEnum.CanBrowseWalletTradingAccountTransactions
            ) && (
              <Menu.Item key='platform' data-test='transactions-platform-transfers-menu-link'>
                <Link to={links.transactionPlatform.url}>Platform Transfers</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.transactionDepositWithdrawal) &&
            hasPermission(
              permissions,
              PermissionsEnum.CanBrowseWalletDepositWithdrawalTransactions
            ) && (
              <Menu.Item
                key='wallet-deposit-withdrawal'
                data-test='transactions-deposits-withdrawals-menu-link'
              >
                <Link to={links.transactionDepositWithdrawal.url}>Deposits/Withdrawals</Link>
              </Menu.Item>
            )}
          {hasPermission(permissions, PermissionsEnum.CanBrowseIBCommissionsTransactions) && (
            <Menu.SubMenu
              title={
                <span data-test='transactions-ib-transactions-menu-link'>IB Transactions</span>
              }
            >
              <Menu.Item key='ib-commissions' data-test='transactions-ib-commissions-menu-link'>
                <Link to={links.transactionIBCommissions.url}>IB Commissions</Link>
              </Menu.Item>
              <Menu.Item key='ib-cashbacks' data-test='transactions-ib-cashback-menu-link'>
                <Link to={links.transactionIBCashBacks.url}>IB CashBacks</Link>
              </Menu.Item>
              {hasPermission(permissions, PermissionsEnum.CanViewRebatePolicies) && (
                <Menu.Item
                  key='ib-cashbacks-settings'
                  data-test='transactions-ib-cashback-settings-menu-link'
                >
                  <Link to={links.transactionIBCashbackSettings.url}>IB CashBacks Settings</Link>
                </Menu.Item>
              )}
            </Menu.SubMenu>
          )}
          {hasPermission(permissions, PermissionsEnum.CanViewSubscriptionRequestCancellations) && (
            <Menu.SubMenu
              title={<span data-test='transactions-subscriptions-menu-link'>Subscriptions</span>}
            >
              <Menu.Item key='unsubscribe-requests' data-test='transaction-subscriptions-menu-link'>
                <Link to={links.transactionUnsubscribeRequests.url}>Unsubscribe Requests</Link>
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {isFeatureEnabled(TenantFeature.transactionBonus) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseBonusTransactions) && (
              <Menu.Item key='bonus' data-test='transactions-bonus-link'>
                <Link to={links.transactionBonus.url}>Bonus</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.transactionPaymentAgent) &&
            hasPermission(permissions, PermissionsEnum.CanBrowsePaymentAgentTransactions) && (
              <Menu.Item key='payment-agent' data-test='transactions-pa-transfers-menu-link'>
                <Link to={links.transactionPaymentAgent.url}>PA Transfers</Link>
              </Menu.Item>
            )}
          {hasPermission(
            permissions,
            PermissionsEnum.CanBrowseBulkTransactionsFileUploadHistory
          ) && (
            <Menu.SubMenu title={<span>Bulk Transactions</span>}>
              {isFeatureEnabled(TenantFeature.transactionBulkTransaction) &&
                hasPermission(
                  permissions,
                  PermissionsEnum.CanBrowseBulkTransactionsFileUploadHistory
                ) && (
                  <Menu.Item key='file-upload-history'>
                    <Link to={links.transactionBulkTransaction.url}>File Upload History</Link>
                  </Menu.Item>
                )}
              {isFeatureEnabled(TenantFeature.transactionBulkWithdrawals) &&
                hasPermission(permissions, PermissionsEnum.CanBrowseBulkWithdrawals) && (
                  <Menu.Item key='bulk-withdrawals'>
                    <Link to={links.transactionBulkWithdrawals.url}>Bulk Withdrawals</Link>
                  </Menu.Item>
                )}
            </Menu.SubMenu>
          )}
          <Menu.Divider />
          <Menu.SubMenu title={<span>Create Transaction</span>}>
            <Menu.Item key='create-cfd' data-test='transactions-cfd-create-transaction-menu-link'>
              <Link to={links.createTransaction.url}>CFD</Link>
            </Menu.Item>
            <Menu.Item key='create-etd' data-test='transactions-etd-create-transaction-menu-link'>
              <Link to={links.createETDTransaction.url}>ETD</Link>
            </Menu.Item>
          </Menu.SubMenu>
        </Menu.SubMenu>
      )}
      {(hasPermission(permissions, PermissionsEnum.CanAddBulkRestrictions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseDeals) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokerProfiles) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseInstruments) ||
        hasPermission(permissions, PermissionsEnum.CanEditDividendPayments)) && (
        <Menu.SubMenu
          className={styles.subMenu}
          title={
            <span data-test='brokerage-menu-link'>
              <span className={classNames(styles.menuItem)}>
                <span className={classNames(styles.menuIcon)}>
                  <ChartColumn />
                </span>
                <span className={classNames(styles.menuText)}>Brokerage</span>
              </span>
            </span>
          }
        >
          {isFeatureEnabled(TenantFeature.brokerageBulkRestrictions) &&
            hasPermission(permissions, PermissionsEnum.CanAddBulkRestrictions) && (
              <Menu.Item key='bulk-restrictions' data-test='brokerage-bulk-restrictions-menu-link'>
                <Link to={links.brokerageBulkRestrictions.url}>Bulk Restrictions</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.brokerageDeals) &&
            hasPermission(permissions, PermissionsEnum.CanViewBrokerageDeals) && (
              <Menu.Item key='deals' data-test='brokerage-deals-menu-link'>
                <Link to={links.brokerageDeals.url}>Deals</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.brokerageIBProfiles) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokerProfiles) && (
              <Menu.Item
                key='introducing-broker-profiles'
                data-test='brokerage-ib-profiles-menu-link'
              >
                <Link to={links.brokerageIBProfiles.url}>IB Profiles</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.brokerageReferralsRestrictions) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseTickmillCompanyReferrals) && (
              <Menu.Item
                key='referrals-restrictions'
                data-test='brokerage-referrals-restrictions-menu-link'
              >
                <Link to={links.brokerageReferralsRestrictions.url}>IB Referrals</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.brokerageInstruments) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseInstruments) && (
              <Menu.Item key='instruments' data-test='brokerage-instruments-menu-link'>
                <Link to={links.brokerageInstruments.url}>Instruments</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.brokerageSwapFreeRating) &&
            hasPermission(permissions, PermissionsEnum.CanViewBrokerageDeals) && (
              <Menu.Item key='swap-free-rating' data-test='brokerage-swap-free-rating-menu-link'>
                <Link to={links.brokerageSwapFreeRating.url}>Swap Free Rating</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.brokerageLeverages) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseInstruments) && (
              <Menu.Item key='leverages' data-test='brokerage-leverages-menu-link'>
                <Link to={links.brokerageLeverages.url}>Leverages</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.brokerageDividends) &&
            hasPermission(permissions, PermissionsEnum.CanEditDividendPayments) && (
              <Menu.Item key='custom-dividends' data-test='brokerage-dividends-menu-link'>
                <Link
                  to={links.brokerageDividends.url.replace(':slug', DividendsTab.CustomDividends)}
                >
                  Corporate Actions
                </Link>
              </Menu.Item>
            )}
          <Menu.SubMenu title='Platforms' key='platforms'>
            {hasPermission(permissions, PermissionsEnum.CanUpdateMetatrader4Settings) && (
              <Menu.SubMenu title='MetaTrader 4' key='meta-trader-4'>
                {hasPermission(permissions, PermissionsEnum.CanUpdateMetatrader4Settings) && (
                  <Menu.Item
                    key='swaps'
                    data-test='brokerage-platforms-meta-trader-4-swaps-menu-link'
                  >
                    <Link to={links.brokeragePlatformsMT4Swaps.url}>Swaps</Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}

            <Menu.SubMenu title='MetaTrader 5' key='meta-trader-5'>
              {hasPermission(permissions, PermissionsEnum.CanUpdateMetatrader5Settings) && (
                <Menu.Item
                  key='swaps'
                  data-test='brokerage-platforms-meta-trader-5-swaps-menu-link'
                >
                  <Link to={links.brokeragePlatformsMT5Swaps.url}>Swaps</Link>
                </Menu.Item>
              )}
              {hasPermission(permissions, PermissionsEnum.CanUpdateServerSessions) && (
                <Menu.Item
                  key='sessions'
                  data-test='brokerage-platforms-meta-trader-5-sessions-menu-link'
                >
                  <Link to={links.brokeragePlatformsMT5Sessions.url}>Sessions</Link>
                </Menu.Item>
              )}
            </Menu.SubMenu>

            {hasPermission(permissions, PermissionsEnum.CanViewTickmillCompanyPlatformTypes) && (
              <Menu.Item
                key='platforms-configuration'
                data-test='brokerage-platforms-servers-configuration-menu-link'
              >
                <Link to={links.brokeragePlatformsConfiguration.url}>Servers Configuration</Link>
              </Menu.Item>
            )}
          </Menu.SubMenu>
        </Menu.SubMenu>
      )}
      {(hasPermission(permissions, PermissionsEnum.CanViewComplianceCountryRisk) ||
        hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults)) && (
        <Menu.SubMenu
          className={styles.subMenu}
          title={
            <span data-test='compliance-menu-link'>
              <span className={classNames(styles.menuItem)}>
                <span className={classNames(styles.menuIcon)}>
                  <FileCheckCorner />
                </span>
                <span className={classNames(styles.menuText)}>Compliance</span>
              </span>
            </span>
          }
        >
          {isFeatureEnabled(TenantFeature.complianceCountryRisk) &&
            hasPermission(permissions, PermissionsEnum.CanViewComplianceCountryRisk) && (
              <Menu.Item key='country-risk' data-test='compliance-country-risk-menu-link'>
                <Link to={links.complianceCountryRisk.url}>Country Risk</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.complianceWorldCheckCases) &&
            hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults) && (
              <Menu.Item key='world-check-cases' data-test='compliance-world-check-cases-menu-link'>
                <Link to={links.complianceWorldCheckCases.url}>World-Check Case Status</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.complianceAppropriatenessTests) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseAppropriatenessTest) && (
              <Menu.Item
                key='appropriateness-tests'
                data-test='compliance-appropriateness-tests-menu-link'
              >
                <Link to={appTestRouteUrl(TestStatusTabs.ACTIVE_TEST_SETS)}>
                  Appropriateness Test
                </Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.disclaimerLossPercentage) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseLossPercentage) && (
              <Menu.Item
                key='disclaimer-loss-percentage'
                data-test='disclaimer-loss-percentage-menu-link'
              >
                <Link to={links.disclaimerLossPercentage.url}>Disclaimer Loss Percentage</Link>
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}
      {isFeatureEnabled(TenantFeature.marketingCampaigns) &&
        (hasPermission(permissions, PermissionsEnum.CanBrowseMarketingCampaigns) ||
          hasPermission(permissions, PermissionsEnum.CanGenerateSGLUrl)) && (
          <Menu.SubMenu
            className={styles.subMenu}
            title={
              <span data-test='marketing-menu-link'>
                <span className={classNames(styles.menuItem)}>
                  <span className={classNames(styles.menuIcon)}>
                    <Megaphone />
                  </span>
                  <span className={classNames(styles.menuText)}>Marketing</span>
                </span>
              </span>
            }
          >
            {hasPermission(permissions, PermissionsEnum.CanBrowseMarketingCampaigns) && (
              <Menu.Item key='campaigns' data-test='marketing-campaigns-menu-link'>
                <Link to={links.marketingCampaigns.url}>Campaigns</Link>
              </Menu.Item>
            )}
            {hasPermission(permissions, PermissionsEnum.CanViewNotifications) && (
              <Menu.Item key='marketingNotifications' data-test='marketing-notifications-menu-link'>
                <Link to={links.marketingNotifications.url}>Notifications</Link>
              </Menu.Item>
            )}
            {hasPermission(permissions, PermissionsEnum.CanViewBonusCampaigns) && (
              <Menu.Item key='bonusModule' data-test='bonus-module-menu-link'>
                <Link to={links.bonusModule.url}>Bonuses</Link>
              </Menu.Item>
            )}
            {hasPermission(permissions, PermissionsEnum.CanViewRewards) && (
              <Menu.Item key='rewardTypeManager' data-test='rewards-hub-menu-link'>
                <Link to={links.rewardTypeManager.url}>Rewards Hub</Link>
              </Menu.Item>
            )}
            {hasPermission(permissions, PermissionsEnum.CanGenerateSGLUrl) && (
              <Menu.Item key='sgl-link-generator' data-test='marketing-sgl-generator-menu-link'>
                <Link to={links.sglLinkGenerator.url}>SGL Link Generator</Link>
              </Menu.Item>
            )}
            {hasPermission(permissions, PermissionsEnum.CanBrowsePromoMaterials) && (
              <Menu.Item key='ib-landing-page' data-test='marketing-ib-landing-page-menu-link'>
                <Link to={links.ibLanding.url}>IB Landing Pages</Link>
              </Menu.Item>
            )}
            {hasPermission(permissions, PermissionsEnum.CanViewReferralMaterials) && (
              <Menu.SubMenu title='Referral Materials'>
                <Menu.Item key='referral-materials' data-test='referral-materials-link'>
                  <Link to={links.referralMaterials.url}>RM List</Link>
                </Menu.Item>
                <Menu.Item
                  key='referral-materials-category-settings'
                  data-test='referral-materials-category-settings-link'
                >
                  <Link to={links.referralMaterialsCategories.url}>Categories settings</Link>
                </Menu.Item>
              </Menu.SubMenu>
            )}
            <Menu.Item key='emails' data-test='marketing-emails-menu-link'>
              <Link to={links.emails.url}>Emails</Link>
            </Menu.Item>
          </Menu.SubMenu>
        )}
      {hasSomePermissions(permissions, [
        PermissionsEnum.CanBrowseVersions,
        PermissionsEnum.CanEditVersions,
        PermissionsEnum.CanBrowseFeatureFlags,
        PermissionsEnum.CanEditFeatureFlags,
        PermissionsEnum.CanBrowsePlatformVersions,
        PermissionsEnum.CanEditPlatformVersions,
      ]) && (
        <Menu.SubMenu
          className={styles.subMenu}
          title={
            <span data-test='product-menu-link'>
              <span className={classNames(styles.menuItem)}>
                <span className={classNames(styles.menuIcon)}>
                  <Blocks />
                </span>
                <span className={classNames(styles.menuText)}>Product</span>
              </span>
            </span>
          }
        >
          {hasSomePermissions(permissions, [
            PermissionsEnum.CanBrowseVersions,
            PermissionsEnum.CanEditVersions,
          ]) && (
            <Menu.Item key='versions' data-test='product-versions-menu-link'>
              <Link to={links.productVersions.url}>Versions</Link>
            </Menu.Item>
          )}
          {hasSomePermissions(permissions, [
            PermissionsEnum.CanBrowseFeatureFlags,
            PermissionsEnum.CanEditFeatureFlags,
          ]) && (
            <Menu.Item key='feature-flags' data-test='product-feature-flags-menu-link'>
              <Link to={links.productFeatureFlags.url}>Feature Flags</Link>
            </Menu.Item>
          )}
          {hasSomePermissions(permissions, [
            PermissionsEnum.CanBrowsePlatformVersions,
            PermissionsEnum.CanEditPlatformVersions,
          ]) && (
            <Menu.Item key='platform-versions' data-test='product-platform-versions-menu-link'>
              <Link to={links.productPlatformVersions.url}>Platform Versions</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {(hasPermission(permissions, PermissionsEnum.CanViewUsers) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseRoles) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseTeams) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseUserTeams) ||
        hasPermission(permissions, PermissionsEnum.CanViewCountryConfiguration) ||
        hasPermission(permissions, PermissionsEnum.CanBrowsePlatformLinks) ||
        hasPermission(permissions, PermissionsEnum.CanViewPaymentControls) ||
        hasPaymentToolsMenuItems) && (
        <Menu.SubMenu
          className={styles.subMenu}
          title={
            <span data-test='administration-menu-link'>
              <span className={classNames(styles.menuItem)}>
                <span className={classNames(styles.menuIcon)}>
                  <Settings />
                </span>
                <span className={classNames(styles.menuText)}>Administration</span>
              </span>
            </span>
          }
        >
          {isFeatureEnabled(TenantFeature.administrationUsers) &&
            hasPermission(permissions, PermissionsEnum.CanViewUsers) && (
              <Menu.Item key='users' data-test='administration-users-menu-link'>
                <Link to={links.administrationUsers.url}>Users</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.administrationUserTeams) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseUserTeams) && (
              <Menu.Item key='user-teams' data-test='administration-user-teams-menu-link'>
                <Link to={links.administrationUserTeams.url}>User Teams</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.administrationRoles) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseRoles) && (
              <Menu.Item key='roles' data-test='administration-roles-menu-link'>
                <Link to={links.administrationRoles.url}>Roles</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.administrationTeams) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseTeams) && (
              <Menu.Item key='teams' data-test='administration-teams-menu-link'>
                <Link to={links.administrationTeams.url}>Teams</Link>
              </Menu.Item>
            )}

          {isFeatureEnabled(TenantFeature.administrationCountries) &&
            hasPermission(permissions, PermissionsEnum.CanViewCountryConfiguration) && (
              <Menu.Item key='countries' data-test='administration-countries-menu-link'>
                <Link to={links.administrationCountries.url}>Countries</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.administrationSubscriptions) &&
            hasPermission(permissions, PermissionsEnum.CanViewSubscriptions) && (
              <Menu.Item key='subscriptions' data-test='subscriptions-menu-link'>
                <Link to={links.administrationSubscriptions.url}>Subscriptions</Link>
              </Menu.Item>
            )}
          {isFeatureEnabled(TenantFeature.administrationPaymentProvidersConfig) &&
            hasPermission(permissions, PermissionsEnum.CanBrowsePaymentProviders) && (
              <Menu.SubMenu title='Payment Providers'>
                <Menu.Item key='payment-providers-config' data-test='psp-menu-config-link'>
                  <Link to={links.administrationPaymentProvidersConfig.url}>Config</Link>
                </Menu.Item>
                <Menu.Item key='payment-providers-ordering' data-test='psp-menu-ordering-link'>
                  <Link to={links.administrationPaymentProvidersOrdering.url}>Ordering</Link>
                </Menu.Item>
              </Menu.SubMenu>
            )}
          {hasPaymentToolsMenuItems && (
            <>
              <Menu.SubMenu title='Payment Tools' key='payment-tools'>
                {isFeatureEnabled(TenantFeature.administrationPaymentControls) &&
                  hasPermission(permissions, PermissionsEnum.CanViewPaymentControls) && (
                    <Menu.SubMenu
                      title={
                        <span data-test='administration-payment-controls-menu-link'>
                          <span className={classNames(styles.menuItem)}>
                            <span className={classNames(styles.menuText)}>Payment Controls</span>
                          </span>
                        </span>
                      }
                    >
                      {isFeatureEnabled(TenantFeature.administrationPaymentControlsPSPCheckState) &&
                        hasPermission(
                          permissions,
                          PermissionsEnum.CanViewPaymentProviderOptions
                        ) && (
                          <Menu.Item
                            key='psp-check-state'
                            data-test='administration-payment-controls-php-check-state-menu-link'
                          >
                            <Link to={links.administrationPaymentControlsPSPCheckState.url}>
                              PSP Check State
                            </Link>
                          </Menu.Item>
                        )}
                      {isFeatureEnabled(
                        TenantFeature.administrationAllowedPaymentProvidersMatrix
                      ) &&
                        hasPermission(permissions, PermissionsEnum.CanBrowsePspGroups) && (
                          <Menu.Item
                            key='allowed-psp'
                            data-test='administration-payment-controls-allowed-psp-menu-link'
                          >
                            <Link to={links.administrationAllowedPaymentProvidersMatrix.url}>
                              Allowed PSP's
                            </Link>
                          </Menu.Item>
                        )}
                    </Menu.SubMenu>
                  )}
                {isFeatureEnabled(TenantFeature.administrationInterestRateConfigControls) &&
                  hasPermission(permissions, PermissionsEnum.CanBrowseInterestRatesConfig) && (
                    <Menu.Item
                      key='interest-rates'
                      data-test='administration-interest-rates-menu-link'
                    >
                      <Link to={links.administrationInterestRateConfigControls.url}>
                        Interest Rates
                      </Link>
                    </Menu.Item>
                  )}
                {isFeatureEnabled(
                  TenantFeature.administrationMlWithdrawalAutomationGlobalControls
                ) &&
                  hasPermission(
                    permissions,
                    PermissionsEnum.CanViewMLWithdrawalAutomationSettings
                  ) && (
                    <Menu.Item
                      key='ml-withdrawal-automation'
                      data-test='administration-ml-withdrawal-automation-link'
                    >
                      <Link to={links.administrationMlWithdrawalAutomationGlobalControls.url}>
                        ML Withdrawal Automation
                      </Link>
                    </Menu.Item>
                  )}
                {isFeatureEnabled(TenantFeature.administrationWithdrawalThresholds) &&
                  hasPermission(permissions, PermissionsEnum.CanViewPaymentProviderThresholds) && (
                    <Menu.Item
                      key='withdrawal-thresholds'
                      data-test='administration-withdrawal-thresholds-menu-link'
                    >
                      <Link to={links.administrationWithdrawalThresholds.url}>
                        Withdrawal Thresholds
                      </Link>
                    </Menu.Item>
                  )}
                {isFeatureEnabled(TenantFeature.administrationTransactionProcessingMetrics) && (
                  <Menu.Item
                    key='transaction-processing-metrics'
                    data-test='administration-transaction-processing-metrics-menu-link'
                  >
                    <Link to={links.administrationTransactionProcessingMetrics.url}>
                      Transaction Processing Metrics
                    </Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
              <Divider style={{ margin: 0 }} />
            </>
          )}
          {isFeatureEnabled(TenantFeature.administrationPlatformLinks) &&
            hasPermission(permissions, PermissionsEnum.CanBrowsePlatformLinks) && (
              <>
                <Menu.SubMenu title='Dev Tools' key='dev-tools'>
                  {isFeatureEnabled(TenantFeature.administrationPlatformLinks) &&
                    hasPermission(permissions, PermissionsEnum.CanBrowsePlatformLinks) && (
                      <Menu.Item
                        key='platform-links'
                        data-test='administration-platform-links-menu-link'
                      >
                        <Link to={links.administrationPlatformLinks.url}>Platform Links</Link>
                      </Menu.Item>
                    )}
                  {isFeatureEnabled(TenantFeature.administrationContactDetails) &&
                    hasPermission(permissions, PermissionsEnum.CanViewContactDetails) && (
                      <Menu.Item
                        key='contact-details'
                        data-test='administration-contact-details-menu-link'
                      >
                        <Link to={links.administrationContactDetails.url}>Contact Details</Link>
                      </Menu.Item>
                    )}
                </Menu.SubMenu>
                <Divider style={{ margin: 0 }} />
              </>
            )}
          {isFeatureEnabled(TenantFeature.administrationRateMarkups) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseRateMarkups) && (
              <Menu.Item key='rate-markups' data-test='administration-rate-markups-link'>
                <Link to={links.administrationRateMarkups.url}>Rate Markups</Link>
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}
    </Menu>
  )
}
